import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  EventEmitter,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { registerFontsFromPptx } from './pptx-font-loader';

/** Minimal surface used from pptxviewjs (avoids static import that patches window.FileReader). */
interface PptxViewerInstance {
  loadFile(buffer: ArrayBuffer): Promise<unknown>;
  render(canvas: HTMLCanvasElement, options: { slideIndex: number }): Promise<unknown>;
  previousSlide(canvas: HTMLCanvasElement): Promise<unknown>;
  nextSlide(canvas: HTMLCanvasElement): Promise<unknown>;
  getSlideCount(): number;
  getCurrentSlideIndex(): number;
  destroy(): void;
}

type PptxViewerCtor = new (options: {
  canvas: HTMLCanvasElement;
  slideSizeMode: string;
  backgroundColor: string;
}) => PptxViewerInstance;

@Component({
  selector: 'mmp5-mm-pptx-viewer',
  templateUrl: './mm-pptx-viewer.component.html',
  styleUrls: ['./mm-pptx-viewer.component.scss'],
})
export class MMPptxViewerComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() data: ArrayBuffer | Blob | null = null;
  @Input('file-name') fileName = 'apresentacao.pptx';
  @Input('can-download') canDownload = true;
  @Output() reachedEnd = new EventEmitter<void>();

  @ViewChild('root') rootEl?: ElementRef<HTMLElement>;
  @ViewChild('stage') stageEl?: ElementRef<HTMLElement>;
  @ViewChild('slideCanvas') slideCanvas?: ElementRef<HTMLCanvasElement>;

  loading = false;
  errorMessage = '';
  currentSlide = 0;
  slideCount = 0;
  zoom = 1;
  isFullscreen = false;

  private viewer: PptxViewerInstance | null = null;
  private loadToken = 0;
  private viewReady = false;
  private pptxViewerCtor: PptxViewerCtor | null = null;
  private loadedBuffer: ArrayBuffer | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private resizeTimer: ReturnType<typeof setTimeout> | null = null;
  private didEmitEnd = false;

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.observeStage();
    void this.reload();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.viewReady) {
      void this.reload();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    this.destroyViewer();
  }

  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    const root = this.rootEl?.nativeElement;
    this.isFullscreen = !!root && document.fullscreenElement === root;
    this.scheduleRerender();
    this.cdr.markForCheck();
  }

  get canGoPrev(): boolean {
    return this.currentSlide > 0;
  }

  get canGoNext(): boolean {
    return this.slideCount > 0 && this.currentSlide < this.slideCount - 1;
  }

  async prevSlide(): Promise<void> {
    if (!this.viewer || !this.canGoPrev || !this.slideCanvas) return;
    await this.viewer.previousSlide(this.slideCanvas.nativeElement);
    this.syncSlideState();
    this.cdr.markForCheck();
  }

  async nextSlide(): Promise<void> {
    if (!this.viewer || !this.canGoNext || !this.slideCanvas) return;
    await this.viewer.nextSlide(this.slideCanvas.nativeElement);
    this.syncSlideState();
    this.cdr.markForCheck();
  }

  zoomIn(): void {
    this.zoom = Math.min(2, Math.round((this.zoom + 0.25) * 100) / 100);
    this.cdr.markForCheck();
  }

  zoomOut(): void {
    this.zoom = Math.max(0.75, Math.round((this.zoom - 0.25) * 100) / 100);
    this.cdr.markForCheck();
  }

  resetZoom(): void {
    this.zoom = 1;
    this.cdr.markForCheck();
  }

  async toggleFullscreen(): Promise<void> {
    const root = this.rootEl?.nativeElement;
    if (!root) return;
    try {
      if (document.fullscreenElement === root) {
        await document.exitFullscreen();
      } else {
        await root.requestFullscreen();
      }
    } catch (err) {
      console.warn('mm-pptx-viewer: fullscreen indisponível', err);
    }
  }

  download(): void {
    if (!this.data) return;
    const blob =
      this.data instanceof Blob
        ? this.data
        : new Blob([this.data], {
            type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = this.fileName || 'apresentacao.pptx';
    a.click();
    URL.revokeObjectURL(url);
  }

  private observeStage(): void {
    const stage = this.stageEl?.nativeElement;
    if (!stage || typeof ResizeObserver === 'undefined') {
      return;
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => this.scheduleRerender());
    this.resizeObserver.observe(stage);
  }

  private scheduleRerender(): void {
    if (!this.viewer || !this.loadedBuffer) {
      return;
    }
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    this.resizeTimer = setTimeout(() => {
      void this.rerenderCurrentSlide();
    }, 120);
  }

  private async reload(): Promise<void> {
    const token = ++this.loadToken;
    this.destroyViewer();
    this.errorMessage = '';
    this.currentSlide = 0;
    this.slideCount = 0;
    this.didEmitEnd = false;
    this.loadedBuffer = null;
    this.zoom = 1;

    if (!this.data) {
      this.loading = false;
      this.cdr.markForCheck();
      return;
    }

    this.loading = true;
    this.cdr.markForCheck();

    try {
      const buffer = await this.toArrayBuffer(this.data);
      if (token !== this.loadToken) return;

      const canvas = this.slideCanvas?.nativeElement;
      if (!canvas) {
        throw new Error('Canvas indisponível');
      }

      this.sizeCanvasToStage(canvas);

      await registerFontsFromPptx(buffer);
      if (token !== this.loadToken) return;

      const PPTXViewer = await this.resolvePptxViewerCtor();
      this.viewer = new PPTXViewer({
        canvas,
        slideSizeMode: 'fit',
        backgroundColor: '#ffffff',
      });
      await this.viewer.loadFile(buffer);
      if (token !== this.loadToken) return;

      this.loadedBuffer = buffer;
      await this.viewer.render(canvas, { slideIndex: 0 });
      if (token !== this.loadToken) return;

      this.syncSlideState();
      this.loading = false;
      this.cdr.markForCheck();
    } catch (err) {
      if (token !== this.loadToken) return;
      console.error('mm-pptx-viewer: falha ao carregar PPTX', err);
      this.errorMessage = 'Não foi possível abrir esta apresentação. Tente baixar o arquivo.';
      this.loading = false;
      this.destroyViewer();
      this.cdr.markForCheck();
    }
  }

  private async rerenderCurrentSlide(): Promise<void> {
    if (!this.viewer || !this.slideCanvas || this.loading) {
      return;
    }
    const canvas = this.slideCanvas.nativeElement;
    const slideIndex = this.currentSlide;
    this.sizeCanvasToStage(canvas);
    try {
      await this.viewer.render(canvas, { slideIndex });
      this.syncSlideState();
      this.cdr.markForCheck();
    } catch (err) {
      console.warn('mm-pptx-viewer: falha ao redimensionar slide', err);
    }
  }

  private sizeCanvasToStage(canvas: HTMLCanvasElement): void {
    const stage = this.stageEl?.nativeElement;
    if (!stage) {
      return;
    }
    const width = Math.max(320, Math.floor(stage.clientWidth));
    const height = Math.max(240, Math.floor(stage.clientHeight));
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
  }

  private syncSlideState(): void {
    if (!this.viewer) {
      this.currentSlide = 0;
      this.slideCount = 0;
      return;
    }
    this.slideCount = this.viewer.getSlideCount();
    this.currentSlide = this.viewer.getCurrentSlideIndex();
    if (!this.didEmitEnd && this.slideCount > 0 && this.currentSlide >= this.slideCount - 1) {
      this.didEmitEnd = true;
      this.reachedEnd.emit();
    }
  }

  private destroyViewer(): void {
    if (this.viewer) {
      try {
        this.viewer.destroy();
      } catch {
        /* ignore */
      }
      this.viewer = null;
    }
    this.loadedBuffer = null;
  }

  private async toArrayBuffer(data: ArrayBuffer | Blob): Promise<ArrayBuffer> {
    if (data instanceof ArrayBuffer) {
      return data;
    }
    return data.arrayBuffer();
  }

  /**
   * pptxviewjs assigns an incompatible helper to window.FileReader on module eval.
   * Load it only when needed and restore the native constructor afterwards so
   * uploads elsewhere in the SPA (CSV, mídia, etc.) keep working.
   */
  private async resolvePptxViewerCtor(): Promise<PptxViewerCtor> {
    if (this.pptxViewerCtor) {
      return this.pptxViewerCtor;
    }
    const nativeFileReader = window.FileReader;
    const mod = await import('pptxviewjs');
    if (window.FileReader !== nativeFileReader) {
      window.FileReader = nativeFileReader;
    }
    patchPptxViewjsWorkarounds();
    this.pptxViewerCtor = mod.PPTXViewer as unknown as PptxViewerCtor;
    return this.pptxViewerCtor;
  }
}

const PPTX_INSET_ATTRS: Array<[string, string]> = [
  ['lIns', 'leftMargin'],
  ['rIns', 'rightMargin'],
  ['tIns', 'topMargin'],
  ['bIns', 'bottomMargin'],
];

/**
 * pptxviewjs 1.1.9: line-height/baseline use inherited paragraph font (~18pt)
 * while fillText uses the run (37pt); `value || defaultInset` treats explicit
 * zero padding as missing; and calculateTextBounds then builds "2% of width"
 * as if canvas px were EMU, collapsing the box to 0 so text leaves the card.
 */
function patchPptxViewjsWorkarounds(): void {
  const ctor = (window as unknown as { CDrawingDocument?: PptxLineHeightCtor }).CDrawingDocument;
  const proto = ctor?.prototype;
  if (!proto) {
    return;
  }
  if (proto.calculateStandardLineHeight && !proto.__mmPptxFontMetricsPatched) {
    const original = proto.calculateStandardLineHeight;
    proto.calculateStandardLineHeight = function patchedLineHeight(
      paraProps: PptxParaProps | undefined,
      wrappedLines?: PptxWrappedLine[] | null,
    ) {
      let maxRun = 0;
      for (const line of wrappedLines || []) {
        for (const runData of line.runs || []) {
          const size = runData.runProps?.fontSize;
          if (typeof size === 'number' && size > 0) {
            maxRun = Math.max(maxRun, size);
          }
        }
      }
      if (paraProps && maxRun > 0) {
        paraProps.fontSize = maxRun;
      }
      return original.call(this, paraProps, wrappedLines);
    };
    proto.__mmPptxFontMetricsPatched = true;
  }
  if (proto.calculateTextBounds && !proto.__mmPptxTextBoundsPatched) {
    proto.calculateTextBounds = function patchedTextBounds(
      this: PptxTextBoundsThis,
      shape: PptxTextShape,
      bounds: PptxBox,
    ) {
      let { x, y, w, h } = bounds || { x: 0, y: 0, w: 0, h: 0 };
      const bodyProps = shape?.textBody?.bodyProperties || shape?.textBody?.bodyPr || {};
      if (this.getShapePreset?.(shape) === 'rtTriangle') {
        y = y + h / 2;
        h = h / 2;
      }
      const scale = this.coordinateSystem?.scale || 1;
      const toPx = (emu: number) => Math.round(this.emuToPixels(emu) * scale);
      const horz = (val: unknown) => {
        if (val === 0 || val === '0') {
          return 0;
        }
        if (val == null) {
          return Math.max(2, Math.round((w || 0) * 0.02));
        }
        return toPx(Number(val));
      };
      const vert = (val: unknown) => {
        if (val === 0 || val === '0') {
          return 0;
        }
        if (val == null) {
          return Math.max(1, Math.round((h || 0) * 0.01));
        }
        return toPx(Number(val));
      };
      const left = horz(bodyProps.leftMargin);
      const right = horz(bodyProps.rightMargin);
      const top = vert(bodyProps.topMargin);
      const bottom = vert(bodyProps.bottomMargin);
      return {
        x: x + left,
        y: y + top,
        w: Math.max((w || 0) - left - right, 0),
        h: Math.max((h || 0) - top - bottom, 0),
        scale,
        margins: { left, right, top, bottom },
      };
    };
    proto.__mmPptxTextBoundsPatched = true;
  }
  if (proto.drawShapeText && !proto.__mmPptxTextClipPatched) {
    const originalDrawShape = proto.drawShapeText;
    proto.drawShapeText = function patchedDrawShapeText(
      this: { graphics?: { context?: CanvasRenderingContext2D } },
      shape: unknown,
      bounds: PptxBox,
    ) {
      const ctx = this.graphics?.context;
      if (ctx && bounds && (bounds.w || 0) > 0 && (bounds.h || 0) > 0) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(bounds.x, bounds.y, bounds.w, bounds.h);
        ctx.clip();
        try {
          return originalDrawShape.call(this, shape, bounds);
        } finally {
          ctx.restore();
        }
      }
      return originalDrawShape.call(this, shape, bounds);
    };
    proto.__mmPptxTextClipPatched = true;
  }
  if (proto.drawTextContent && proto.estimateTextHeightPx && proto.parseRunProperties && !proto.__mmPptxAutofitPatched) {
    const originalDraw = proto.drawTextContent;
    const originalParseRun = proto.parseRunProperties;
    proto.parseRunProperties = function patchedParseRun(
      this: { _mmAutofitScale?: number },
      run: unknown,
      paraProps: PptxParaProps | undefined,
      shape?: unknown,
    ) {
      const props = originalParseRun.call(this, run, paraProps, shape);
      const scale = this._mmAutofitScale;
      if (props && typeof scale === 'number' && scale > 0 && scale < 0.999 && typeof props.fontSize === 'number') {
        return { ...props, fontSize: props.fontSize * scale };
      }
      return props;
    };
    proto.drawTextContent = function patchedDrawText(
      this: {
        _mmAutofitScale?: number;
        estimateTextHeightPx: (textBody: unknown, width: number) => number;
      },
      textBody: unknown,
      bounds: { w?: number; h?: number },
    ) {
      const previous = this._mmAutofitScale;
      this._mmAutofitScale = 1;
      try {
        if (textBody && bounds && (bounds.h || 0) > 1 && (bounds.w || 0) > 1) {
          let scale = 1;
          for (let i = 0; i < 12; i++) {
            this._mmAutofitScale = scale;
            const height = this.estimateTextHeightPx(textBody, bounds.w || 0);
            if (!(height > (bounds.h || 0) + 0.5)) {
              break;
            }
            scale = Math.max(0.4, scale * 0.88);
          }
          this._mmAutofitScale = scale;
        }
        return originalDraw.call(this, textBody, bounds);
      } finally {
        this._mmAutofitScale = previous;
      }
    };
    proto.__mmPptxAutofitPatched = true;
  }
  hookPptxProcessorInsets();
}

function wrapParseBodyProperties(proto: PptxParseBodyProto): void {
  if (!proto.parseBodyProperties || proto.__mmPptxInsetsPatched) {
    return;
  }
  const originalParse = proto.parseBodyProperties;
  proto.parseBodyProperties = function patchedBodyProps(txBodyElement: Element) {
    const props = originalParse.call(this, txBodyElement) || {};
    const bodyPr = txBodyElement?.querySelector?.('bodyPr, a\\:bodyPr');
    if (bodyPr) {
      for (const [attr, key] of PPTX_INSET_ATTRS) {
        if (bodyPr.hasAttribute(attr)) {
          props[key] = parseInt(bodyPr.getAttribute(attr) || '0', 10) || 0;
        }
      }
    }
    return props;
  };
  proto.__mmPptxInsetsPatched = true;
}

function patchParseBodyPropertiesOn(root: object): void {
  const seen = new Set<object>();
  const stack: object[] = [root];
  let steps = 0;
  while (stack.length && steps < 4000) {
    steps += 1;
    const obj = stack.pop() as object;
    if (!obj || typeof obj !== 'object' || seen.has(obj)) {
      continue;
    }
    if (typeof Element !== 'undefined' && obj instanceof Element) {
      continue;
    }
    seen.add(obj);
    const proto = Object.getPrototypeOf(obj) as PptxParseBodyProto | null;
    if (proto && typeof proto.parseBodyProperties === 'function') {
      wrapParseBodyProperties(proto);
    }
    for (const value of Object.values(obj)) {
      if (value && typeof value === 'object' && !seen.has(value)) {
        stack.push(value);
      }
    }
  }
}

function hookPptxProcessorInsets(): void {
  const proc = (window as unknown as { PPTXProcessor?: PptxProcessorCtor }).PPTXProcessor;
  if (!proc?.prototype?.processFile || proc.prototype.__mmInsetsHooked) {
    return;
  }
  const originalProcess = proc.prototype.processFile;
  proc.prototype.processFile = async function patchedProcessFile(...args: unknown[]) {
    if (typeof this._ensureInitialized === 'function') {
      await this._ensureInitialized();
    } else if (typeof this.initialize === 'function') {
      await this.initialize();
    }
    if (this.processor) {
      patchParseBodyPropertiesOn(this.processor);
    }
    return originalProcess.apply(this, args);
  };
  proc.prototype.__mmInsetsHooked = true;
}

interface PptxParaProps {
  fontSize?: number;
  [key: string]: unknown;
}

interface PptxWrappedLine {
  runs?: Array<{ runProps?: { fontSize?: number } }>;
}

interface PptxParseBodyProto {
  parseBodyProperties?: (txBodyElement: Element) => Record<string, unknown>;
  __mmPptxInsetsPatched?: boolean;
}

interface PptxProcessorCtor {
  prototype: {
    processFile: (...args: unknown[]) => Promise<unknown>;
    __mmInsetsHooked?: boolean;
  };
}

interface PptxBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface PptxTextShape {
  textBody?: {
    bodyProperties?: Record<string, unknown>;
    bodyPr?: Record<string, unknown>;
  };
}

interface PptxTextBoundsThis {
  coordinateSystem?: { scale?: number };
  emuToPixels: (emu: number) => number;
  getShapePreset?: (shape: unknown) => string | undefined;
}

interface PptxLineHeightCtor {
  prototype: {
    calculateStandardLineHeight: (
      paraProps: PptxParaProps | undefined,
      wrappedLines?: PptxWrappedLine[] | null,
    ) => number;
    calculateTextBounds?: (shape: PptxTextShape, bounds: PptxBox) => PptxBox;
    drawShapeText?: (shape: unknown, bounds: PptxBox) => void;
    drawTextContent?: (textBody: unknown, bounds: { w?: number; h?: number }) => void;
    estimateTextHeightPx?: (textBody: unknown, width: number) => number;
    parseRunProperties?: (run: unknown, paraProps?: PptxParaProps, shape?: unknown) => PptxParaProps;
    __mmPptxFontMetricsPatched?: boolean;
    __mmPptxAutofitPatched?: boolean;
    __mmPptxTextBoundsPatched?: boolean;
    __mmPptxTextClipPatched?: boolean;
  };
}
